import { Download, FileText, CheckCircle, AlertCircle, Loader2, Send, RotateCcw, Trash2, EyeOff, MessageSquare, ArrowRight, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchEvaluations, submitSession, deleteSession } from '@/lib/api/simulation';
import { useState } from 'react';
import { toast } from 'sonner';
import { useDebriefTemplate, useSessionLinks } from '@/hooks/useLearningLoop';
import { normaliseEvaluationFeedbackList } from '@/lib/evaluationFormatting';

interface EvaluationTabProps {
  agentId: string;
  userId: string;
}

// Debrief Section Component - Part of the Learning Loop
interface DebriefSectionProps {
  evaluationId: string;
  sessionId: string;
  overallScore: number | null;
  hasDebrief: boolean;
  debriefSessionId?: string;
  coachingSessionId?: string;
}

function DebriefSection({
  evaluationId,
  sessionId,
  overallScore,
  hasDebrief,
  debriefSessionId,
  coachingSessionId
}: DebriefSectionProps) {
  const navigate = useNavigate();
  const { data: debriefTemplate, isLoading } = useDebriefTemplate('sales');

  // Don't show if no debrief template available
  if (isLoading || !debriefTemplate) return null;

  // If already has a debrief, show link to view it
  if (hasDebrief && debriefSessionId) {
    return (
      <Card className="mt-6 mb-6 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-full">
                <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h4 className="font-medium text-purple-800 dark:text-purple-300">Debrief Completed</h4>
                <p className="text-sm text-purple-600 dark:text-purple-400">
                  You've reflected on this session
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/coaching/results/${debriefSessionId}`)}
              className="border-purple-300 text-purple-700 hover:bg-purple-100 dark:border-purple-700 dark:text-purple-300"
            >
              View Debrief
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Show debrief CTA
  return (
    <Card className="mt-6 mb-6 overflow-hidden border-2 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-transparent dark:from-purple-950/30">
      <div className="p-4">
        <div className="flex items-start gap-4">
          <div className="p-2.5 bg-purple-100 dark:bg-purple-900/40 rounded-full">
            <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-purple-800 dark:text-purple-300 mb-1">
              Time to Reflect
            </h4>
            <p className="text-sm text-purple-600 dark:text-purple-400 mb-3">
              {overallScore && overallScore >= 70
                ? "Great performance! A debrief session will help you solidify what worked well."
                : "Reflect on this session with your AI coach to identify growth opportunities."}
            </p>
            <Button
              onClick={() => {
                // Store the evaluation context and navigate to debrief
                sessionStorage.setItem('debrief_evaluation_id', evaluationId);
                sessionStorage.setItem('debrief_session_id', sessionId);
                if (coachingSessionId) {
                  sessionStorage.setItem('debrief_coaching_id', coachingSessionId);
                }
                // Navigate to a debrief session (using the debrief template)
                navigate(`/coaching/debrief?evaluation=${evaluationId}&session=${sessionId}`);
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Start Debrief Session
            </Button>
          </div>
        </div>
      </div>

      {/* Learning Loop Progress */}
      <div className="px-4 py-2.5 bg-purple-100/50 dark:bg-purple-900/20 border-t border-purple-200 dark:border-purple-800">
        <div className="flex items-center justify-center gap-4 text-xs text-purple-700 dark:text-purple-400">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle className="w-3 h-3 text-white" />
            </div>
            <span className={coachingSessionId ? 'font-medium' : 'opacity-60'}>PREPARE</span>
          </div>
          <ArrowRight className="w-3 h-3" />
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle className="w-3 h-3 text-white" />
            </div>
            <span className="font-medium">PRACTICE</span>
          </div>
          <ArrowRight className="w-3 h-3" />
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center animate-pulse">
              <Play className="w-2.5 h-2.5 text-white" />
            </div>
            <span className="font-medium text-purple-800 dark:text-purple-300">REFLECT</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function downloadEvaluationReport(evaluation: any, sessionNumber: number) {
  const date = new Date(evaluation.evaluated_at).toLocaleString();
  const criteriaScores = evaluation.detailed_evaluation?.criteria_scores || [];
  const strengths = normaliseEvaluationFeedbackList(evaluation.detailed_evaluation?.strengths);
  const improvements = normaliseEvaluationFeedbackList(evaluation.detailed_evaluation?.areas_for_improvement);
  const overallFeedback = evaluation.detailed_evaluation?.overall_feedback || '';
  
  const report = `
SIMULATION EVALUATION REPORT
============================

Session: ${sessionNumber}
Date: ${date}
Overall Score: ${evaluation.overall_score}

PERFORMANCE BREAKDOWN
--------------------
${criteriaScores.map((c: any) => 
  `${c.criterion}: ${c.score}/${c.max_score}\n  ${c.feedback || 'No feedback'}`
).join('\n\n') || 'No criteria scores available'}

STRENGTHS
---------
${strengths.map((s: string, i: number) => `${i + 1}. ${s}`).join('\n') || 'No strengths recorded'}

AREAS FOR IMPROVEMENT
--------------------
${improvements.map((a: string, i: number) => `${i + 1}. ${a}`).join('\n') || 'No areas for improvement recorded'}

OVERALL FEEDBACK
---------------
${overallFeedback || 'No overall feedback provided'}

${evaluation.transcript ? '\nTRANSCRIPT\n----------\n' + evaluation.transcript : ''}
`.trim();

  const blob = new Blob([report], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `evaluation-session-${sessionNumber}-${Date.now()}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function EvaluationTab({ agentId, userId }: EvaluationTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTranscript, setSelectedTranscript] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  const { data: evaluations, isLoading, error } = useQuery({
    queryKey: ['evaluations', agentId, userId],
    queryFn: () => fetchEvaluations(agentId, userId),
    refetchInterval: 15000, // Auto-refresh every 15 seconds
  });

  const handleSubmitSession = async (evaluationId: string) => {
    try {
      setIsSubmitting(evaluationId);
      const result = await submitSession(evaluationId);
      
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['evaluations', agentId, userId] });
      } else {
        // Error already shown by submitSession, but handle auth specifically
        if (result.message === 'Not authenticated') {
          toast.error('Your session has expired. Please log in again.');
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(null);
    }
  };

  const handleDeleteSession = async (evaluationId: string) => {
    try {
      const result = await deleteSession(evaluationId);
      
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['evaluations', agentId, userId] });
        setShowDeleteConfirm(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 size={48} className="text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <AlertCircle size={48} className="text-destructive mx-auto mb-4" />
        <p className="text-destructive">Failed to load evaluations. Please try again.</p>
      </div>
    );
  }

  if (!evaluations || evaluations.length === 0) {
    return (
      <div className="text-center p-12">
        <FileText size={64} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-xl text-muted-foreground mb-2">No evaluations yet</p>
        <p className="text-muted-foreground">Complete a simulation to get your first evaluation!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="session-1" className="w-full">
        <TabsList className="w-full justify-start mb-6">
          {evaluations.map((evaluation, index) => (
            <TabsTrigger 
              key={index} 
              value={`session-${evaluations.length - index}`}
              className="flex items-center gap-2"
            >
              Session {evaluations.length - index}
              {evaluation.is_submitted ? (
                <Badge variant="default" className="bg-green-500 text-xs ml-1">✓</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs ml-1">Practice</Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {evaluations.map((evaluation, index) => {
          const sessionNumber = evaluations.length - index;
          const criteriaScores = evaluation.detailed_evaluation?.criteria_scores || [];
          const strengths = normaliseEvaluationFeedbackList(evaluation.detailed_evaluation?.strengths);
          const improvements = normaliseEvaluationFeedbackList(evaluation.detailed_evaluation?.areas_for_improvement);
          const overallFeedback = evaluation.detailed_evaluation?.overall_feedback || '';

          return (
            <TabsContent key={evaluation.id} value={`session-${sessionNumber}`}>
              <Card className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    Session {sessionNumber}
                  </h3>
                <p className="text-sm text-muted-foreground">
                  {new Date(evaluation.evaluated_at).toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-primary">{evaluation.overall_score}</div>
                <div className="text-sm text-muted-foreground">Overall Score</div>
              </div>
            </div>

            {/* Criteria Scores */}
            {criteriaScores.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-foreground mb-4">Performance Breakdown</h4>
                <div className="space-y-4">
                  {criteriaScores.map((criterion, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">{criterion.criterion}</span>
                        <span className="text-sm font-bold text-primary">
                          {criterion.score}/{criterion.max_score}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${(criterion.score / criterion.max_score) * 100}%` }}
                        />
                      </div>
                      {criterion.feedback && (
                        <p className="text-xs text-muted-foreground">{criterion.feedback}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strengths */}
            {strengths.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-success mb-3 flex items-center gap-2">
                  <CheckCircle size={18} />
                  Strengths
                </h4>
                <ul className="space-y-2">
                  {strengths.map((strength, idx) => (
                    <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                      <span className="text-success mt-1">✓</span>
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Areas for Improvement */}
            {improvements.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-warning mb-3 flex items-center gap-2">
                  <AlertCircle size={18} />
                  Areas for Improvement
                </h4>
                <ul className="space-y-2">
                  {improvements.map((improvement, idx) => (
                    <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                      <span className="text-warning mt-1">→</span>
                      <span>{improvement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Overall Feedback */}
            {overallFeedback && (
              <div className="mb-6">
                <h4 className="font-semibold text-foreground mb-3">Overall Feedback</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{overallFeedback}</p>
              </div>
            )}

            {/* Practice Session Actions - Only show if session is not submitted */}
            {!evaluation.is_submitted && (
              <Card className="mt-6 mb-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                <div className="p-4">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-full">
                      <EyeOff className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-blue-800 dark:text-blue-300">🔒 Practice Session</h4>
                      <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                        Only you can see this session. Practice as many times as you need,
                        then submit your best call for manager review when you're ready.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      onClick={() => handleSubmitSession(evaluation.id)}
                      disabled={isSubmitting === evaluation.id}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isSubmitting === evaluation.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Submit for Manager Review
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={() => navigate(`/simulation/${evaluation.simulation_id || evaluation.agent_id}`)}
                      className="flex-1"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Practice Again
                    </Button>
                    
                    <Button 
                      variant="ghost"
                      onClick={() => setShowDeleteConfirm(evaluation.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Submitted Session Indicator */}
            {evaluation.is_submitted && (
              <Card className="mt-6 mb-6 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                <div className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-full">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-green-800 dark:text-green-300">Submitted for Manager Review</h4>
                      <p className="text-sm text-green-600 dark:text-green-400">
                        Submitted on {new Date(evaluation.submitted_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Debrief CTA - Learning Loop Reflection */}
            <DebriefSection
              evaluationId={evaluation.id}
              sessionId={evaluation.session_id}
              overallScore={evaluation.overall_score}
              hasDebrief={!!evaluation.debrief_session_id}
              debriefSessionId={evaluation.debrief_session_id}
              coachingSessionId={evaluation.coaching_session_id}
            />

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-border">
              {evaluation.transcript && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSelectedTranscript(evaluation.transcript)}
                  >
                    <FileText size={16} />
                    View Transcript
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const content = `CONVERSATION TRANSCRIPT
========================
Session: ${sessionNumber}
Date: ${new Date(evaluation.evaluated_at).toLocaleDateString()}

${evaluation.transcript}`;
                      const blob = new Blob([content], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `transcript-session-${sessionNumber}-${evaluation.id.substring(0, 8)}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <Download size={16} />
                    Download Transcript
                  </Button>
                </>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => downloadEvaluationReport(evaluation, sessionNumber)}
              >
                <Download size={16} />
                Download Report
                </Button>
              </div>
            </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!showDeleteConfirm} onOpenChange={(open) => !open && setShowDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Practice Session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this practice session and its evaluation. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => showDeleteConfirm && handleDeleteSession(showDeleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transcript Dialog */}
      <Dialog open={!!selectedTranscript} onOpenChange={(open) => !open && setSelectedTranscript(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Conversation Transcript</DialogTitle>
            <DialogDescription className="sr-only">View the full conversation transcript for this evaluation</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <pre className="whitespace-pre-wrap text-sm text-foreground font-mono">{selectedTranscript}</pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
